/**
 * NextStepNudge — the handoff.
 *
 * After a page tour finishes, this offers the next unfinished setup step and a
 * button that takes the user straight to it. It reads the same lane-ordered
 * steps as the SetupDock, so the two never disagree about what's next. Shown in
 * the dock's corner; the host hides the dock while it's up so they don't stack.
 */

import React from 'react';
import { ArrowRight, X } from 'lucide-react';
import type { SetupStepCopy } from './overlayCopy';

interface Props {
  step: SetupStepCopy;
  onNavigate: (route: string) => void;
  onDismiss: () => void;
}

export const NextStepNudge: React.FC<Props> = ({ step, onNavigate, onDismiss }) => (
  <div className="fixed bottom-4 right-4 z-[1035] w-[320px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl bg-navy shadow-2xl dark:bg-[#1A1D26] dark:ring-1">
    <div className="flex items-start gap-2.5 px-4 py-3.5">
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] font-bold uppercase tracking-wide text-primary">
          Next up
        </div>
        <div className="mt-1 text-[13px] font-bold text-white">{step.title}</div>
        <p className="mt-1 text-[11.5px] leading-relaxed text-white/60">
          {step.body}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onNavigate(step.route)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-primaryHover"
          >
            {step.cta}
            <ArrowRight className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="text-[11px] text-white/40 hover:text-white/70"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="flex-none rounded p-0.5 text-white/40 hover:bg-card/10 hover:text-white"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
);

export default NextStepNudge;
